import { type ReactNode, useMemo } from 'react';

import { useIntl } from 'react-intl';

import type {
  OutcomesSectionData,
  TaskSummarySectionData,
  UsersSectionData,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import type {
  MetricCardItem,
  MetricsPanelProps,
  SectionStatus,
} from '@src/packages/dashboard/components/ReportSummary';

import { ORDER_KPIS, USER_KPIS } from '../constants';
import { formatCompactDuration, getOrderKpiValues, getUserKpiValuesFromSection } from '../historySelectors';
import type { OpenTaskDrilldown } from '../types';

const EMPTY_VALUE = '—';

interface KpisSource<TData> {
  data?: TData;
  status: SectionStatus;
}

/** KPI panel: order and user indicators, each opening its drill-down when available. */
export const useTasksKpis = (
  orderKpis: KpisSource<TaskSummarySectionData>,
  userKpis: KpisSource<UsersSectionData>,
  outcomes: OutcomesSectionData | undefined,
  openDrilldown: OpenTaskDrilldown,
): MetricsPanelProps => {
  const intl = useIntl();

  return useMemo(() => {
    const hint = (label: string, available: boolean) => intl.formatMessage(
      {
        id: available
          ? 'dashboards.tasks.summary.drilldown.available'
          : 'dashboards.tasks.summary.drilldown.unavailable',
      },
      { label },
    );

    const values = orderKpis.data ? getOrderKpiValues(orderKpis.data) : undefined;
    const orderValues: Record<string, ReactNode> = {
      orders: values?.orders ?? EMPTY_VALUE,
      completed: values?.completed ?? EMPTY_VALUE,
      supervisorClosed: values?.supervisorClosed ?? EMPTY_VALUE,
      partial: outcomes?.result.partial ?? EMPTY_VALUE,
      canceled: outcomes?.result.canceled ?? EMPTY_VALUE,
      completion: values?.completionRate == null ? EMPTY_VALUE : `${Math.round(values.completionRate)}%`,
      late: values?.late ?? EMPTY_VALUE,
      delay: values?.delay ?? EMPTY_VALUE,
      duration: values?.averageDurationMinutes == null ? EMPTY_VALUE : formatCompactDuration(values.averageDurationMinutes),
    };
    const orderItems: MetricCardItem[] = ORDER_KPIS.filter(
      ({ key }) => key !== 'supervisorClosed' || (values?.supervisorClosed ?? 0) > 0,
    ).map(({ key, filter }) => {
      const messageGroup = key === 'partial' || key === 'canceled' ? 'result' : 'metric';
      const label = intl.formatMessage({ id: `dashboards.tasks.summary.${messageGroup}.${key}` });
      return {
        key,
        value: orderValues[key],
        label,
        detail: intl.formatMessage({ id: `dashboards.tasks.summary.${messageGroup}.${key}.detail` }),
        onClick: filter ? () => openDrilldown({ kind: 'orders', filter, title: label }) : undefined,
        actionLabel: hint(label, true),
        unavailableHint: hint(label, false),
      };
    });

    const userValues = userKpis.data ? getUserKpiValuesFromSection(userKpis.data) : undefined;
    const userItems: MetricCardItem[] = USER_KPIS.map(({ key, filter }) => {
      const label = intl.formatMessage({ id: `dashboards.tasks.summary.users.metric.${key}` });
      return {
        key,
        value: !userValues
          ? EMPTY_VALUE
          : key === 'average'
            ? intl.formatNumber(userValues.averageOrders, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
            : userValues[key],
        label,
        detail: intl.formatMessage({ id: `dashboards.tasks.summary.users.metric.${key}.detail` }),
        onClick: filter ? () => openDrilldown({ kind: 'users', filter, title: label }) : undefined,
        actionLabel: hint(label, true),
        unavailableHint: hint(label, false),
      };
    });

    return {
      ariaLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.metrics' }),
      groups: [
        {
          label: intl.formatMessage({ id: 'dashboards.tasks.summary.metricGroup.orders' }),
          columns: 8,
          items: orderItems,
          status: orderKpis.status,
        },
        {
          label: intl.formatMessage({ id: 'dashboards.tasks.summary.metricGroup.users' }),
          items: userItems,
          status: userKpis.status,
        },
      ],
    };
  }, [intl, openDrilldown, orderKpis, outcomes, userKpis]);
};
